import { usePrepareCart } from '@cannect/hooks/usePrepareCart'
import useQueryString from '@cannect/hooks/useQueryString'
import { ArrowLeft } from 'lucide-react'
import {
  AddressStep,
  CartSteps,
  OrderSummary,
  OrderSummaryStep3,
  PaymentFinish,
  PaymentStep,
  PixPayment
} from 'pages/NewCartCOD/v2'
import CreditCardPayment from 'pages/NewCartCheckout/PaymentMethod/CreditCard'
import { useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { isBillingAddressValid, type TBillingAddress } from './BillingAddressForm'
import { applyPixDiscountToAmount, getPixDiscount } from '@cannect/utils/pixDiscount'
import { attachProgramOrderIfNeeded, readProgramPaymentSummary, type TProgramPaymentSummary } from './programCart'
import { ProgramIncludedCard } from './screens'

// Dados já informados na trilha; pré-preenchem o Endereço quando o pedido não os traz.
export type TProgramPatient = { fullName?: string; cpf?: string; email?: string; phone?: string; birth?: string }

interface ProgramCartFlowProps {
  patient?: TProgramPatient
  // Voltar na etapa Resumo devolve à identificação da trilha.
  onBack: () => void
  // Cobrança recusada ou que não pôde ser gerada: o fluxo mostra a tela de erro de pagamento.
  onFailed: (summary: TProgramPaymentSummary) => void
  // O pedido já está pago (ex.: a pessoa voltou ao passo depois de pagar): segue para a confirmação sem nova cobrança.
  onPaid: (summary: TProgramPaymentSummary) => void
}

const noop = async () => ({ success: 'true' })

// O endereço de entrega da etapa 2 também serve de cobrança do cartão: o gateway exige um endereço válido.
const toBillingAddress = (address: Record<string, string> | null): TBillingAddress | null => {
  if (!address) return null
  const billing: TBillingAddress = {
    zip_code: address.cep || '',
    street: address.street || '',
    address_number: address.number || '',
    complement: address.complement || '',
    district: address.neighborhood || '',
    city: address.city || '',
    state: address.state || ''
  }
  return isBillingAddressValid(billing) ? billing : null
}

// Carrinho do /carrinho (Resumo, Endereço, Escolha de Pagamento, Finalização) dentro da trilha: o pedido é lido pelo
// uuid (?cart_id=) e as chamadas usam as rotas públicas, então não exige login nem sai da trilha. O Resumo troca a
// linha única do produto pela lista do que está incluso no programa.
export const ProgramCartFlow = ({ patient, onBack, onFailed, onPaid }: ProgramCartFlowProps) => {
  const { lastOrderCreated, getLastOrder, isUpdate, updateCartCoupon, coupon, removeCoupon } = usePrepareCart()
  const cartId = useQueryString().filters?.cart_id
  const items = lastOrderCreated?.items || []
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('')
  const [addressData, setAddressData] = useState<Record<string, string> | null>(null)
  const topRef = useRef<HTMLDivElement>(null)
  // Pedido que já esteve na tela: o pagamento limpa o carrinho (clearOrderData), e isso não é "carregando".
  const knownOrderIdRef = useRef<number | null>(null)
  const formMethods = useForm<{ cvv1: string; cvv2: string }>({ defaultValues: { cvv1: '', cvv2: '' } })

  // O provider pode já trazer outro carrinho (o da conta logada, ou de uma visita anterior): buscar só quando está
  // vazio mostrava esses itens no lugar do pedido do programa. Sempre carrega o pedido do ?cart_id= ao abrir.
  const [orderLoaded, setOrderLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false
    setOrderLoaded(false)
    Promise.resolve(getLastOrder()).finally(() => {
      if (!cancelled) setOrderLoaded(true)
    })
    return () => {
      cancelled = true
    }
  }, [cartId])

  useEffect(() => {
    if (!orderLoaded) return
    if (lastOrderCreated?.id) {
      knownOrderIdRef.current = lastOrderCreated.id
      return
    }

    // O pagamento acabou de limpar o carrinho: se foi esse pedido que foi pago, segue direto para a confirmação em
    // vez de esperar o redirecionamento do formulário de cartão (3 s + tentativas de vínculo).
    const summary = readProgramPaymentSummary()
    if (knownOrderIdRef.current && summary?.orderId === knownOrderIdRef.current) onPaid(summary)
  }, [lastOrderCreated?.id, orderLoaded])

  const paidPayment = (lastOrderCreated?.payments || []).find(
    (payment: { status?: string }) => payment.status === 'paid'
  )

  useEffect(() => {
    // Antes de carregar o pedido do cart_id o provider ainda pode ter outro pedido (já pago) de uma visita anterior.
    if (!orderLoaded || !paidPayment || !lastOrderCreated?.id) return undefined

    let cancelled = false
    const run = async () => {
      await attachProgramOrderIfNeeded(lastOrderCreated)
      if (cancelled) return
      const isPix = paidPayment.method === 'pix'
      const pixDiscount = getPixDiscount(lastOrderCreated)
      onPaid({
        orderId: lastOrderCreated.id,
        method: isPix ? 'pix' : 'credit',
        // O Pix do programa é cobrado com desconto; a confirmação mostra o valor pago, não o do pedido.
        amount:
          isPix && pixDiscount.applied
            ? applyPixDiscountToAmount(Number(lastOrderCreated.amount), pixDiscount.percentage)
            : Number(lastOrderCreated.amount)
      })
    }
    run()

    return () => {
      cancelled = true
    }
  }, [paidPayment?.id, lastOrderCreated?.id, orderLoaded])

  // O botão de avançar fica no resumo (embaixo no celular): sem isto a etapa seguinte abria já rolada para o fim.
  useEffect(() => {
    topRef.current?.scrollIntoView({ block: 'start' })
  }, [currentStep, orderLoaded])

  // Pedido que já estava pago ao salvar o endereço (a API recusa com "Pedido já foi finalizado"): confirma em vez de
  // deixar a pessoa presa no carrinho.
  const handleAlreadyPaid = async () => {
    if (!lastOrderCreated?.id) return
    await attachProgramOrderIfNeeded(lastOrderCreated)
    onPaid({
      orderId: lastOrderCreated.id,
      method: paidPayment?.method === 'pix' ? 'pix' : 'credit',
      amount: Number(lastOrderCreated.amount)
    })
  }

  const billingAddress = useMemo(() => toBillingAddress(addressData), [addressData])

  const goBack = () => (currentStep === 1 ? onBack() : setCurrentStep((step) => Math.max(1, step - 1)))

  const renderFinalization = () => {
    if (paidPayment) {
      return <p className="m-0 text-sm text-[#6B6560]">Pagamento recebido. Confirmando o seu programa…</p>
    }
    if (selectedPaymentMethod === 'pix') return <PixPayment selectedMethod="pix" cartItems={items} />
    if (selectedPaymentMethod === 'credit') {
      return (
        <CreditCardPayment
          loadingContainer={false}
          paymentMethod="credit_card"
          finishOrderByPaymentMethod={noop}
          disabledPix
          disabledBoleto
          billingAddressRequired
          embedded
          onPaymentError={({ installments }) =>
            onFailed({
              orderId: lastOrderCreated?.id,
              method: 'credit',
              amount: Number(lastOrderCreated?.amount),
              installments
            })
          }
          billingAddress={billingAddress as Record<string, string> | null}
        />
      )
    }
    return <PaymentFinish selectedMethod={selectedPaymentMethod} cartItems={items} shippingAddress={addressData} />
  }

  if (!orderLoaded || !lastOrderCreated?.id) {
    return (
      <p className="m-0 text-sm text-[#6B6560]">
        {knownOrderIdRef.current ? 'Pagamento recebido. Confirmando o seu programa…' : 'Carregando seu pedido…'}
      </p>
    )
  }

  return (
    <FormProvider {...formMethods}>
      <div ref={topRef} className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex w-fit items-center gap-2 rounded-md border-0 bg-transparent py-1 text-sm font-medium text-primary-800 hover:underline">
            <ArrowLeft className="size-5" />
            Voltar
          </button>
          <CartSteps currentStep={currentStep} onStepClick={(step) => step <= currentStep && setCurrentStep(step)} />
        </div>

        <div className="flex flex-col gap-4 lg:grid lg:grid-cols-12 lg:gap-8">
          <div className="min-w-0 lg:col-span-7">
            {currentStep === 1 && <ProgramIncludedCard />}
            {currentStep === 2 && (
              <AddressStep
                onNextStep={() => setCurrentStep(3)}
                onPreviousStep={() => setCurrentStep(1)}
                onAddressDataChange={setAddressData}
                initialAddressData={addressData ?? (patient as Record<string, string> | undefined)}
              />
            )}
            {currentStep === 3 && (
              <PaymentStep
                onNextStep={() => setCurrentStep(4)}
                onPreviousStep={() => setCurrentStep(2)}
                onPaymentMethodSelect={setSelectedPaymentMethod}
              />
            )}
            {currentStep === 4 && renderFinalization()}
          </div>

          <div className="min-w-0 lg:col-span-5">
            {currentStep === 1 ? (
              <OrderSummary
                itens={items}
                onApplyCoupon={updateCartCoupon}
                onProceedToPayment={() => setCurrentStep(2)}
                isUpdate={isUpdate}
                coupon={coupon}
                onRemoveCoupon={removeCoupon}
                lastOrderCreated={lastOrderCreated}
              />
            ) : (
              <OrderSummaryStep3
                itens={items}
                onApplyCoupon={updateCartCoupon}
                isUpdate={isUpdate}
                currentStep={currentStep}
                onNextStep={() => setCurrentStep(currentStep === 2 ? 3 : 4)}
                onPreviousStep={() => setCurrentStep(1)}
                addressData={addressData}
                onRemoveCoupon={removeCoupon}
                coupon={coupon}
                selectedPaymentMethod={selectedPaymentMethod}
                onProgramAlreadyPaid={handleAlreadyPaid}
              />
            )}
          </div>
        </div>
      </div>
    </FormProvider>
  )
}
